import React from 'react';

import { getConsentDecisionInsights } from '../../../../api/v2/program_insights';
import useProgramInsightsMetric from '../use_program_insights_metric';
import KpiCards from '../kpi_cards';
import styles from './index.module.css';

const REASON_COLOR = 'var(--pp-ocean-tide)';
const RATING_COLOR = 'var(--pp-fresh-mint)';

// A labeled horizontal bar: a left label, a proportional track, and a right value.
function Bar({ label, value, fraction, color }) {
  return (
    <li className={styles.bar}>
      <span className={styles.barLabel}>{label}</span>
      <div className={styles.track}>
        <div className={styles.fill} style={{ width: `${fraction * 100}%`, backgroundColor: color }} />
      </div>
      <span className={styles.barValue}>{value}</span>
    </li>
  );
}

// The Consent Decision Insights drill-in body, embedded inside the Engagement Continuation card.
export default function ConsentDecisionInsights({ filters }) {
  const { data, status } = useProgramInsightsMetric(getConsentDecisionInsights, filters);

  if (status === 'loading') {
    return <p className={styles.state}>Loading…</p>;
  }

  if (status === 'error') {
    return <p className={styles.stateError}>We could not load this drill-in. Try again shortly.</p>;
  }

  const { totals, reasons, rating_distribution: ratingDistribution } = data;
  const hasDecided = totals.decided_not_to_continue > 0;
  const maxReason = Math.max(...reasons.map((reason) => reason.count), 1);
  const maxRating = Math.max(...ratingDistribution.map((bucket) => bucket.count), 1);
  const ratingsDescending = [...ratingDistribution].sort((a, b) => b.rating - a.rating);

  return (
    <div className={styles.panel}>
      <KpiCards
        cards={[
          { label: 'Pre-Consent Participants', value: totals.pre_consent_participants.toLocaleString() },
          { label: 'Decided Not to Continue', value: totals.decided_not_to_continue.toLocaleString() },
          { label: '% Not Continuing', value: `${totals.percent_not_continuing}%` },
          {
            label: 'Avg Mentor Satisfaction',
            value: totals.average_mentor_satisfaction != null ? `${totals.average_mentor_satisfaction} ★` : '—',
          },
        ]}
      />

      <div className={styles.columns}>
        <section className={styles.column}>
          <p className={styles.sectionLabel}>Reasons for not continuing</p>
          {hasDecided ? (
            <ul className={styles.bars}>
              {reasons.map((reason) => (
                <Bar
                  key={reason.key}
                  label={reason.label}
                  value={`${reason.count.toLocaleString()} (${reason.percent}%)`}
                  fraction={reason.count / maxReason}
                  color={REASON_COLOR}
                />
              ))}
            </ul>
          ) : (
            <p className={styles.state}>No participants in this window decided not to continue.</p>
          )}
        </section>

        <section className={styles.column}>
          <p className={styles.sectionLabel}>Mentor satisfaction distribution</p>
          {hasDecided ? (
            <ul className={styles.bars}>
              {ratingsDescending.map((bucket) => (
                <Bar
                  key={bucket.rating}
                  label={`${bucket.rating} ★`}
                  value={bucket.count.toLocaleString()}
                  fraction={bucket.count / maxRating}
                  color={RATING_COLOR}
                />
              ))}
            </ul>
          ) : (
            <p className={styles.state}>No mentor ratings are recorded for participants who did not continue.</p>
          )}
        </section>
      </div>
    </div>
  );
}
